import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import React from 'react';
import { Box, List } from '@mui/material';
import { gql, DocumentNode } from '@apollo/client';
import { InvoiceEditLineItemHeader } from './InvoiceEditLineItemHeader';
import styles from './InvoiceEditLineItems.module.scss';
import { InvoiceEditDraggableLineItem } from './InvoiceEditDraggableLineItem';
import { UseLineItems, useLineItemsInvoiceFragment } from '../useLineItems';
import { lineItemsAreMutable } from './helpers';
import { InvoiceEditLineItem } from './InvoiceEditLineItem/InvoiceEditLineItem';
import { useDeviceDetection } from '../../../hooks/useDeviceDetection';
import { InvoiceEditLineItemsInvoiceFragment } from '#types';
import { InvoiceLineItemAddLineItemButton } from './InvoiceLineItemAddLineItemButton';
import rpcShared from '@rockpapercoin/rpc-shared';

const invoiceEditLineItemsInvoiceFragment = gql`
	fragment invoiceEditLineItemsInvoice on Invoice {
		id
		status
		organization {
			country
		}
		...invoiceEditLineItemInvoice
		...useLineItemsInvoice
	}
	${ useLineItemsInvoiceFragment }
	${ InvoiceEditLineItem.fragments.invoiceEditLineItemInvoiceFragment }
`;

type InvoiceEditLineItemsProps = Pick<
UseLineItems,
| 'lineItemsDetails'
| 'onDragEnd'
| 'onAddLineItem'
| 'onRemoveLineItem'
| 'onUpdateLineItem'
| 'onBlur'
> & {
	invoiceId?: string;
	invoice?: Omit<InvoiceEditLineItemsInvoiceFragment, 'id'> & { id?: string };
};

type InvoiceEditLineItemsPropsWithFragment =
	React.FC<InvoiceEditLineItemsProps> & {
		fragments: {
			invoiceEditLineItemsInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceEditLineItems: InvoiceEditLineItemsPropsWithFragment = ( {
	invoiceId,
	invoice,
	lineItemsDetails,
	onDragEnd,
	onAddLineItem,
	onRemoveLineItem,
	onUpdateLineItem,
	onBlur,
} ) => {
	const { isMobile } = useDeviceDetection();

	const maxLineItems = 60;

	const currencyLabel = rpcShared.invoices.getCurrencyLabel(
		invoice?.organization?.country
	);
	const rateLabel = `Rate${ currencyLabel }`;

	if ( !invoice?.lineItems || invoice.lineItems.length === 0 ) {
		return null;
	}
	const isMutable = lineItemsAreMutable( !!invoiceId, invoice?.status );
	return (
		<DragDropContext onDragEnd={ onDragEnd }>
			<Box className={ styles.container } data-cy='invoice-edit-line-items'>
				<InvoiceEditLineItemHeader
					isMobile={ isMobile }
					isMutable={ isMutable }
					rateLabel={ rateLabel }
				/>
				{ isMutable ? (
					<>
						<Droppable droppableId={ 'lineItems' }>
							{ ( provided ) => (
								<List
									{ ...provided.droppableProps }
									ref={ provided.innerRef }
									disablePadding
								>
									{ invoice?.lineItems?.map( ( lineItem, index ) => (
										<InvoiceEditDraggableLineItem
											invoiceExists={ !!invoiceId }
											invoice={ invoice }
											autoFocus={
												lineItemsDetails.find(
													( lineItemDetails ) =>
														lineItemDetails.id === lineItem.id
												)?.autoFocus || false
											}
											index={ index }
											key={ lineItem.id }
											draggableId={ lineItem.id }
											lineItem={ lineItem }
											lineItemDetails={ lineItemsDetails.find(
												( lineItemDetails ) => lineItemDetails.id === lineItem.id
											) }
											onUpdate={ onUpdateLineItem( lineItem.id ) }
											onRemove={ onRemoveLineItem }
											onBlur={ onBlur }
											rateLabel={ rateLabel }
										/>
									) ) }
									{ provided.placeholder }
								</List>
							) }
						</Droppable>
						<InvoiceLineItemAddLineItemButton
							className={ styles.addItemButton }
							variant='text'
							onClick={ onAddLineItem }
							data-cy='invoice-edit-line-item-add'
							disabled={ lineItemsDetails.length >= maxLineItems }
						>
							Add Line Item
						</InvoiceLineItemAddLineItemButton>
					</>
				) : (
					<List disablePadding>
						{ invoice?.lineItems?.map( ( lineItem ) => (
							<InvoiceEditLineItem
								invoiceExists={ !!invoiceId }
								autoFocus={
									lineItemsDetails.find(
										( lineItemDetails ) => lineItemDetails.id === lineItem.id
									)?.autoFocus || false
								}
								key={ lineItem.id }
								lineItem={ lineItem }
								lineItemDetails={ lineItemsDetails.find(
									( lineItemDetails ) => lineItemDetails.id === lineItem.id
								) }
								onUpdate={ onUpdateLineItem( lineItem.id ) }
								onRemove={ onRemoveLineItem }
								onBlur={ onBlur }
								rateLabel={ rateLabel }
							/>
						) ) }
					</List>
				) }
			</Box>
		</DragDropContext>
	);
};

InvoiceEditLineItems.fragments = {
	invoiceEditLineItemsInvoiceFragment,
};
